import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";

// ────────────────────────────────────────────────────────────────────────
// EditModeContext — two mutually-exclusive edit modes + navigation lockdown
// while an edit mode is active. Visible to all visitors for the moment
// (the toggle buttons can be hidden behind admin auth later — the context
// itself stays untouched).
// ────────────────────────────────────────────────────────────────────────
const EditModeContext = createContext({
  imageEditMode: false,
  textEditMode: false,
  toggleImage: () => {},
  toggleText: () => {},
  // Imperative API used by /admin/images "View" deep-links: enables
  // image edit mode + queues a specific slot for immediate editing.
  // Any component (e.g. PreviewHighlighter) can call this to jump the
  // editor straight to the correct image on page load.
  openImageEditor: () => {},
  pendingEditorSlot: null,
  consumePendingEditorSlot: () => {},
});

export const useEditMode = () => useContext(EditModeContext);

export const EditModeProvider = ({ children }) => {
  const [imageEditMode, setImageEditMode] = useState(false);
  const [textEditMode, setTextEditMode] = useState(false);
  // Pending slot payload written by openImageEditor(). AutoImageEditor
  // subscribes and, once the DOM has the matching <img> mounted, opens
  // the ImageEditModal — then calls consumePendingEditorSlot() to
  // clear it. Keeping the payload here (vs. a bespoke ref) means the
  // React reconciler drives the hand-off, so we never race the modal
  // against a fresh route change.
  const [pendingEditorSlot, setPendingEditorSlot] = useState(null);

  const toggleImage = useCallback(() => {
    setImageEditMode((prev) => {
      const next = !prev;
      if (next) setTextEditMode(false);
      return next;
    });
  }, []);

  const toggleText = useCallback(() => {
    setTextEditMode((prev) => {
      const next = !prev;
      if (next) setImageEditMode(false);
      return next;
    });
  }, []);

  const openImageEditor = useCallback((slot, opts = {}) => {
    if (!slot) return;
    setImageEditMode(true);
    setTextEditMode(false);
    // Timestamp defeats stale-payload equality: if the same slot is
    // requested twice in a row (e.g. rapid navigations from
    // /admin/images) the second call still triggers the effect.
    setPendingEditorSlot({
      slot,
      currentUrl: opts.currentUrl || "",
      alt: opts.alt || "",
      // Signals to AutoImageEditor that the caller has already
      // exhausted its DOM lookup for this slot — skip re-polling and
      // open the modal on the next tick.
      resolved: !!opts.resolved,
      ts: Date.now(),
    });
  }, []);

  const consumePendingEditorSlot = useCallback(() => {
    setPendingEditorSlot(null);
  }, []);

  const anyMode = imageEditMode || textEditMode;

  // ── Body classes (gives CSS a hook + helps debugging) ────────────────
  useEffect(() => {
    const body = document.body;
    body.classList.toggle("edit-mode-on", anyMode);
    body.classList.toggle("edit-mode-image", imageEditMode);
    body.classList.toggle("edit-mode-text", textEditMode);
    return () => {
      body.classList.remove("edit-mode-on", "edit-mode-image", "edit-mode-text");
    };
  }, [anyMode, imageEditMode, textEditMode]);

  // ── Block all navigation while editing (capture phase beats React Router) ──
  useEffect(() => {
    if (!anyMode) return undefined;

    const isAllowed = (target) =>
      target.closest('[role="dialog"]') ||
      target.closest('[data-testid^="editable-text-"]') ||
      target.closest('[data-edit-allow="true"]') ||
      // Inline-text-edit bindings own their own click/mousedown
      // handlers (focus + caret placement). If we block here we also
      // strip the user's ability to place a caret in a button/link
      // they're trying to edit.
      target.closest('[data-cms-bound="1"]');

    const block = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
    };

    const handler = (e) => {
      const target = e.target;
      if (!target) return;
      // Middle-click on a link → still block
      if ((e.button === 1 || e.type === "auxclick") && target.closest && target.closest("a")) {
        return block(e);
      }
      if (isAllowed(target)) return;
      if (target.closest && target.closest("a")) return block(e);
    };

    const events = ["click", "auxclick", "mousedown", "pointerdown", "dragstart"];
    events.forEach((ev) => document.addEventListener(ev, handler, true));
    return () => events.forEach((ev) => document.removeEventListener(ev, handler, true));
  }, [anyMode]);

  // ── Lock history so back/forward buttons don't fly away from the editor ──
  useEffect(() => {
    if (!anyMode) return undefined;
    const sentinel = { _editLock: true, ts: Date.now() };
    window.history.pushState(sentinel, "", window.location.href);
    const onPop = () => {
      window.history.pushState(sentinel, "", window.location.href);
    };
    const onKey = (e) => {
      // Alt+← / Alt+→ → swallow
      if (e.altKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        e.preventDefault();
      }
    };
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [anyMode]);

  const value = useMemo(
    () => ({
      imageEditMode, textEditMode,
      toggleImage, toggleText,
      openImageEditor, pendingEditorSlot, consumePendingEditorSlot,
    }),
    [imageEditMode, textEditMode, toggleImage, toggleText, openImageEditor, pendingEditorSlot, consumePendingEditorSlot]
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
};

// Backwards-compat alias so existing imports keep working
export const CmsProvider = EditModeProvider;
export const useCms = useEditMode;
